import {useRouteLoaderData} from '@github-ui/react-router'
import type {DataTag} from '@tanstack/react-query'

import type {RouteQueryKey} from '../query-key'
import type {ConfigQueryFetchedResultData} from './data-router-types'
import type {MainQueryResult} from './main-query'
import type {AnyQueryRoute, RouteMatchData} from './query-route'

/**
 * Returns the {@link RouteQueryKey} for a named query on the given route, or
 * `undefined` if the route is not currently mounted.
 *
 * This reads directly from the (serializable) loader data emitted by
 * {@link QueryRoute}'s loader. Prefer this over building a `queryConfig`
 * via `useQueriesConfig` when all you need is the key (e.g. for
 * `queryClient.getQueryData(queryKey)` or `invalidateQueries({queryKey})`).
 *
 * Use cases:
 * - Looking up cached data via `queryClient.getQueryData(queryKey)` from a
 *   component that lives above (or beside) the owning route, where calling
 *   `useRouteQuery` isn't appropriate.
 */
export function useRouteQueryKey<Config extends AnyQueryRoute, QueryName extends string & keyof Config['queries']>(
  route: Config,
  queryName: QueryName,
) {
  const data = useRouteLoaderData<RouteMatchData<Config> | undefined>(route.id)

  type QueryResultData = ConfigQueryFetchedResultData<Config, QueryName>
  // mainQuery is stored as {meta, payload} object with a selector that only exposes the payload by default. Normally
  // the meta is abstracted away, but in this case we need the correct type since set/getQueryData work directly with
  // the underlying store entry.
  type QueryFnData = QueryName extends 'mainQuery' ? MainQueryResult<QueryResultData> : QueryResultData

  return data?.queries?.[queryName]?.queryKey as DataTag<RouteQueryKey, QueryFnData> | undefined
}
